// Client-side session state: the currently signed-in user (mirrors the worker's
// fake session) and a small unread-notification counter for the tab badge.
// The worker remains the source of truth; this is a convenience cache.

import type { User } from '@shared/models';
import { api } from './api';

let currentUser: User | null = null;
let unreadCount = 0;
const changeListeners = new Set<() => void>();

export function getCurrentUser(): User | null {
  return currentUser;
}

export function getUnreadCount(): number {
  return unreadCount;
}

export function onSessionChange(fn: () => void): () => void {
  changeListeners.add(fn);
  return () => changeListeners.delete(fn);
}

function emitChange(): void {
  for (const fn of changeListeners) fn();
}

/** Restore any existing worker session (none on a fresh load). */
export async function restore(): Promise<User | null> {
  currentUser = await api.call('auth.currentUser', undefined);
  if (currentUser) await refreshUnread();
  emitChange();
  return currentUser;
}

export async function signIn(accountId: string): Promise<User> {
  currentUser = await api.call('auth.signIn', { accountId });
  await refreshUnread();
  emitChange();
  return currentUser;
}

export async function signOut(): Promise<void> {
  await api.call('auth.signOut', undefined);
  currentUser = null;
  unreadCount = 0;
  emitChange();
}

export function setCurrentUser(user: User): void {
  currentUser = user;
  emitChange();
}

export async function refreshUnread(): Promise<void> {
  if (!currentUser) {
    unreadCount = 0;
    return;
  }
  const list = await api.call('notifications.list', undefined);
  unreadCount = list.filter((n) => !n.read).length;
  emitChange();
}

// Keep the unread badge live as the worker pushes notifications.
api.on('notification', () => {
  unreadCount += 1;
  emitChange();
});
